import React, { useMemo } from 'react';
import { BackgroundThemeRevision, ColorId } from 'referral-types';
import { rgbStringToHex } from '../../../../../utils/colorUtils';
import { useTheme } from '../../../../../context/theme/useTheme';
import { useBodyStyleVersion } from '../../../../../hooks/useBodyStyleVersion';
import { getCssVariableForColorId } from '../../../../../constants';

const DualRipplesBackground: React.FC<{ themeRevision?: Partial<BackgroundThemeRevision> }> = (props) => {
  const { colorId1, colorId2 } = props.themeRevision || {};
  const { isDarkMode } = useTheme();
  const bodyStyleVersion = useBodyStyleVersion();

  const computedColors = useMemo(() => {
    const colorStyles1 = getCssVariableForColorId(colorId1 || ColorId.Primary2, isDarkMode);
    const colorStyles2 = getCssVariableForColorId(colorId2 || ColorId.Primary1, isDarkMode);

    const rootStyles = getComputedStyle(document.body);
    const c1 = rgbStringToHex(rootStyles.getPropertyValue(colorStyles1).trim() || '255 51 51');
    const c2 = rgbStringToHex(rootStyles.getPropertyValue(colorStyles2).trim() || '24 132 214');

    return {
      c1,
      c2,
    };
  }, [colorId1, colorId2, isDarkMode, bodyStyleVersion]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        backgroundColor: computedColors.c1,
        pointerEvents: 'none',
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 2000 1500"
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
        }}
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <radialGradient id="dual-ripples-a" gradientUnits="objectBoundingBox">
            <stop offset="0.9" stopColor={computedColors.c2} stopOpacity="0" />
            <stop offset="1" stopColor={computedColors.c2} stopOpacity="0.2" />
          </radialGradient>
          <linearGradient id="dual-ripples-c" x1="1" y1="0" x2="0" y2="0" gradientTransform="rotate(0,0.5,0.5)">
            <stop offset="0" stopColor={computedColors.c1} />
            <stop offset=".5" stopColor={computedColors.c1} />
            <stop offset="1" stopColor={computedColors.c1} />
          </linearGradient>
          <g id="dual-ripples-b" fill="url(#dual-ripples-a)">
            <circle cx="1560" cy="1560" r="2500" />
            <circle cx="1520" cy="1520" r="2450" />
            <circle cx="1480" cy="1480" r="2400" />
            <circle cx="1440" cy="1440" r="2350" />
            <circle cx="1400" cy="1400" r="2300" />
            <circle cx="1360" cy="1360" r="2250" />
            <circle cx="1320" cy="1320" r="2200" />
            <circle cx="1280" cy="1280" r="2150" />
            <circle cx="1240" cy="1240" r="2100" />
            <circle cx="1200" cy="1200" r="2050" />
            <circle cx="1160" cy="1160" r="2000" />
            <circle cx="1120" cy="1120" r="1950" />
            <circle cx="1080" cy="1080" r="1900" />
            <circle cx="1040" cy="1040" r="1850" />
            <circle cx="1000" cy="1000" r="1800" />
            <circle cx="960" cy="960" r="1750" />
            <circle cx="920" cy="920" r="1700" />
            <circle cx="880" cy="880" r="1650" />
            <circle cx="840" cy="840" r="1600" />
            <circle cx="800" cy="800" r="1550" />
            <circle cx="760" cy="760" r="1500" />
            <circle cx="720" cy="720" r="1450" />
            <circle cx="680" cy="680" r="1400" />
            <circle cx="640" cy="640" r="1350" />
            <circle cx="600" cy="600" r="1300" />
            <circle cx="560" cy="560" r="1250" />
            <circle cx="520" cy="520" r="1200" />
            <circle cx="480" cy="480" r="1150" />
            <circle cx="440" cy="440" r="1100" />
            <circle cx="400" cy="400" r="1050" />
            <circle cx="360" cy="360" r="1000" />
            <circle cx="320" cy="320" r="950" />
            <circle cx="280" cy="280" r="900" />
            <circle cx="240" cy="240" r="850" />
            <circle cx="200" cy="200" r="800" />
            <circle cx="160" cy="160" r="750" />
            <circle cx="120" cy="120" r="700" />
            <circle cx="80" cy="80" r="650" />
            <circle cx="40" cy="40" r="600" />
            <circle cx="0" cy="0" r="550" />
          </g>
        </defs>
        <rect fill="url(#dual-ripples-c)" width="2000" height="1500" />
        <g transform="rotate(180 1000 750)">
          <use href="#dual-ripples-b" x="-500" y="-1000" />
          <use href="#dual-ripples-b" x="-2500" y="-1000" transform="scale(-1 1)" />
        </g>
      </svg>
    </div>
  );
};

export default DualRipplesBackground;
